import { Capacitor, registerPlugin } from '@capacitor/core';
import { API_URL } from './api';
import { getOfflineAccountScope, isOfflineScopeCurrent, scopedOfflineKey } from './scopedOfflineQueue';

const Native = registerPlugin('BerestoStripeTerminal');
const preferenceKey = scope => scopedOfflineKey('berestoNativeTerminal', scope);
let session = null;
let listenerHandles = [];
let listenerSetup = null;
let paymentInFlight = false;
let paymentCancelled = false;
let configurationInFlight = false;
const fail = (code, message = 'Lecteur indisponible. Vérifiez le paiement avant toute nouvelle tentative.') => Object.assign(new Error(message), { code });
const assertCurrent = scope => { if (!scope || !isOfflineScopeCurrent(scope)) throw fail('TERMINAL_ACCOUNT_CHANGED'); };
const scopeMatches = (left, right) => left?.stripeAccountId === right?.stripeAccountId && left?.locationId === right?.locationId && left?.livemode === right?.livemode;
const assertConfig = config => {
  if (!/^acct_[A-Za-z0-9]+$/.test(config?.stripeAccountId || '') || !/^tml_[A-Za-z0-9]+$/.test(config?.locationId || '') || typeof config?.livemode !== 'boolean') throw fail('TERMINAL_INVALID_SCOPE');
};
export function isNativeTerminalAvailable() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('BerestoStripeTerminal');
}
export function readNativeTerminalPreference(scope = getOfflineAccountScope()) {
  try {
    const value = JSON.parse(localStorage.getItem(preferenceKey(scope)) || 'null');
    assertConfig(value);
    return ['usb', 'bluetooth'].includes(value.transport) && typeof value.serialNumber === 'string' && /^tmr_[A-Za-z0-9]+$/.test(value.readerId || '') ? value : null;
  } catch { return null; }
}
async function ensureListeners() {
  if (listenerSetup) return listenerSetup;
  listenerSetup = (async () => {
    listenerHandles.push(await Native.addListener('connectionTokenRequested', async request => {
      const current = session;
      let reply = { requestId: request.requestId, failed: true };
      try {
        if (!current || !scopeMatches(current, request)) throw fail('TERMINAL_ACCOUNT_CHANGED');
        assertCurrent(current.scope);
        const response = await current.fetchWithAuth(`${API_URL}/api/payments/terminal/connection-token`, {
          method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locationId: current.locationId }),
        });
        const payload = await response.json();
        assertCurrent(current.scope);
        if (session !== current || !response.ok || !scopeMatches(current, payload) || typeof payload.secret !== 'string' || !payload.secret) throw fail('TERMINAL_TOKEN_SCOPE_MISMATCH');
        reply = { requestId: request.requestId, secret: payload.secret, stripeAccountId: current.stripeAccountId, locationId: current.locationId, livemode: current.livemode };
      } catch { /* Native callback receives only a fixed failure; never provider response text. */ }
      await Native.provideConnectionToken(reply).catch(() => {});
    }));
    listenerHandles.push(await Native.addListener('readersDiscovered', event => {
      if (session && scopeMatches(session, event) && isOfflineScopeCurrent(session.scope)) session.onReaders?.(event.readers || []);
    }));
    listenerHandles.push(await Native.addListener('terminalStatus', event => {
      if (session && scopeMatches(session, event) && isOfflineScopeCurrent(session.scope)) session.onStatus?.(event);
    }));
  })().catch(error => { listenerSetup = null; throw error; });
  return listenerSetup;
}
export function configureNativeTerminal(options) { return configureSession(options, false); }
async function configureSession({ locationId, stripeAccountId, livemode, fetchWithAuth, transport = 'usb', onReaders, onStatus, scope = getOfflineAccountScope() }, forPayment) {
  if (!isNativeTerminalAvailable()) throw fail('TERMINAL_ANDROID_REQUIRED', 'Le WisePad 3 nécessite l’application Android BERESTO.');
  assertCurrent(scope);
  const next = { locationId, stripeAccountId, livemode, fetchWithAuth, transport, onReaders, onStatus, scope };
  assertConfig(next);
  if (typeof fetchWithAuth !== 'function' || !['usb', 'bluetooth'].includes(transport)) throw fail('TERMINAL_INVALID_CONFIGURATION');
  if (configurationInFlight || (paymentInFlight && !forPayment)) throw fail('TERMINAL_BUSY');
  configurationInFlight = true;
  // The token provider must point at the new authenticated scope before native init requests a token.
  const previous = session;
  session = next;
  try {
    await ensureListeners();
    await Native.initialize({ locationId, stripeAccountId, livemode, transport });
    assertCurrent(scope);
  } catch (error) { session = previous; throw fail(error.code || 'TERMINAL_INITIALIZATION_FAILED'); }
  finally { configurationInFlight = false; }
  return Native.getStatus();
}
export async function discoverNativeTerminalReaders() {
  if (!session) throw fail('TERMINAL_NOT_CONFIGURED');
  assertCurrent(session.scope);
  return Native.discoverReaders();
}
export async function connectNativeTerminalReader({ serialNumber }) {
  const current = session;
  if (!current || typeof serialNumber !== 'string' || !serialNumber.trim()) throw fail('TERMINAL_DISCOVERY_REQUIRED');
  assertCurrent(current.scope);
  const result = await Native.connectReader({ serialNumber });
  assertCurrent(current.scope);
  if (session !== current || !scopeMatches(current, result) || !/^tmr_[A-Za-z0-9]+$/.test(result.reader?.readerId || '') || result.reader.serialNumber !== serialNumber) throw fail('TERMINAL_READER_SCOPE_MISMATCH');
  // Only non-secret device identifiers are retained; token and PI client_secret stay in memory.
  const preference = { stripeAccountId: current.stripeAccountId, locationId: current.locationId, livemode: current.livemode, transport: current.transport, serialNumber, readerId: result.reader.readerId };
  localStorage.setItem(preferenceKey(current.scope), JSON.stringify(preference));
  return { ...result.reader, ...preference };
}
async function reconnectPreference(preference, onStatus) {
  const current = session;
  let timer;
  const previousReaders = current.onReaders;
  try {
    return await new Promise((resolve, reject) => {
      let connecting = false;
      timer = setTimeout(() => reject(fail('TERMINAL_READER_NOT_FOUND', 'Allumez le WisePad 3 associé et vérifiez le câble USB ou le Bluetooth.')), 35000);
      current.onReaders = readers => {
        previousReaders?.(readers);
        if (connecting || !readers.some(reader => reader.serialNumber === preference.serialNumber)) return;
        connecting = true;
        connectNativeTerminalReader({ serialNumber: preference.serialNumber }).then(resolve, reject);
      };
      onStatus?.({ state: 'reconnecting' });
      discoverNativeTerminalReaders().catch(reject);
    });
  } finally { clearTimeout(timer); current.onReaders = previousReaders; }
}
export async function processTerminalPayment({ clientSecret, readerId, locationId, stripeAccountId, livemode, amountCents, fetchWithAuth, onStatus, scope = getOfflineAccountScope() }) {
  if (!isNativeTerminalAvailable()) throw fail('TERMINAL_ANDROID_REQUIRED', 'Le WisePad 3 nécessite l’application Android BERESTO.');
  assertConfig({ locationId, stripeAccountId, livemode });
  assertCurrent(scope);
  if (paymentInFlight) throw fail('TERMINAL_BUSY');
  if (!/^pi_[A-Za-z0-9]+_secret_[A-Za-z0-9]+$/.test(clientSecret || '') || !/^tmr_[A-Za-z0-9]+$/.test(readerId || '') || !Number.isSafeInteger(amountCents) || amountCents < 50) throw fail('TERMINAL_INVALID_PAYMENT');
  const preference = readNativeTerminalPreference(scope);
  if (!preference || !scopeMatches(preference, { locationId, stripeAccountId, livemode }) || preference.readerId !== readerId) throw fail('TERMINAL_READER_SETUP_REQUIRED', 'Associez ce WisePad 3 dans les réglages Android de l’établissement.');
  paymentInFlight = true;
  paymentCancelled = false;
  try {
    await configureSession({ ...preference, fetchWithAuth, onStatus, scope }, true);
    let state = await Native.getStatus();
    if (!state.reader) { await reconnectPreference(preference, onStatus); state = await Native.getStatus(); }
    assertCurrent(scope);
    if (!scopeMatches(state, { locationId, stripeAccountId, livemode }) || state.reader?.readerId !== readerId) throw fail('TERMINAL_READER_SCOPE_MISMATCH');
    if (paymentCancelled) throw fail('TERMINAL_COLLECTION_CANCELLED');
    const result = await Native.processPayment({ clientSecret, readerId, locationId, stripeAccountId, livemode, amountCents });
    assertCurrent(scope);
    // Never report "paid" here. The caller must retrieve the canonical backend receipt.
    return { paymentIntentId: result.paymentIntentId, status: result.status, needsReconciliation: true };
  } catch (error) { throw fail(error.code || 'TERMINAL_RECONCILE_REQUIRED'); }
  finally { paymentInFlight = false; }
}
export async function cancelTerminalCollection() {
  paymentCancelled = true;
  if (!isNativeTerminalAvailable()) return;
  await Native.cancelCollection();
}
export async function disconnectNativeTerminal() {
  if (!isNativeTerminalAvailable()) return;
  if (paymentInFlight) throw fail('TERMINAL_BUSY');
  await Native.disconnect();
  session = null;
}
